'use client';

import { type FC } from 'react';
import { Icon } from '../../../../ui/Icon';
import { IconUserBonefish } from '@maverick/icons/dist/IconUserBonefish';
import { IconActivityVector } from '@maverick/icons/dist/IconActivityVector';
import { IconInvertedPin } from '@maverick/icons/dist/IconInvertedPin';
import { IconHamburguerMenu } from '@maverick/icons/dist/IconHamburguerMenu';
import { IconChatBox } from '@maverick/icons/dist/IconChatBox';
import { IconGear } from '@maverick/icons/dist/IconGear';
import styles from './DashboardMenu.module.scss';
import { Footer } from '../../../../ui/Footer';
import { useIsMobile } from '../../../../../shared/hooks/useIsMobile';
import { useRouter } from 'next/navigation';
import { GaEvent } from '@maverick/utils';
import { AppState } from '../../../../../lib/store/slices/App.slice';
import { scrollToHash } from '../../../../../shared/utils/HashNavigation';

const menu = [
	{
		label: 'ACTIVITY TIMELINE',
		hash: 'activity-timeline',
		icon: IconActivityVector,
	},
	{
		label: 'PLAN A VISIT',
		hash: 'plan-a-visit',
		icon: IconInvertedPin,
	},
	{
		label: 'HOW IT WORKS',
		hash: 'how-it-works',
		icon: IconGear,
	},
	{
		label: 'FAQS',
		hash: 'faqs',
		icon: IconHamburguerMenu,
	},
	{
		label: 'MISSING POINTS',
		hash: 'missing-points-form',
		icon: IconChatBox,
	},
];

interface DashboardMenuProps {
	userRewardsInfo: AppState['userRewardsInfo'];
	setMenuClicked: (value: boolean) => void;
}

export const DashboardMenu: FC<DashboardMenuProps> = ({ userRewardsInfo, setMenuClicked }) => {
	const router = useRouter();
	const pointsToReward = 350;
	const isMobile = useIsMobile();

	const handleClickMemberProfile = () => {
		history.replaceState(null, '', window.location.pathname);
		router.push('/dashboard/member-profile');
	};

	const handleClickEvent = (hash: string) => {
		GaEvent.MenuNavigation(hash);
		if (hash === 'visit-tracker') {
			GaEvent.DineRewardsPointsTracker(userRewardsInfo.userPoints);
		}
	};

	const handleClickMenuItem = (hash: string) => {
		const newHash = `#${hash}`;
		setMenuClicked(true);
		handleClickEvent(hash);

		window.location.hash = newHash;
		if (window.location.href.includes('member-profile')) {
			router.push(`/dashboard${newHash}`);
		}
		scrollToHash(hash, isMobile);
	};

	const keyDownMenuHandler = (ev: React.KeyboardEvent<HTMLDivElement>, hash: string) => {
		if (ev.key === 'Enter' || ev.key === ' ') {
			ev.preventDefault();
			handleClickMenuItem(hash);
		}
	};

	const handleProgressBarPoints = (): number => {
		if (userRewardsInfo.userPoints >= 350) {
			return 100;
		}
		return (userRewardsInfo.userPoints / pointsToReward) * 100;
	};

	return (
		<div className={styles['container']}>
			<div data-testid='header' className={styles['header']}>
				<div
					data-testid='dine-rewards-logo'
					className={styles['logo']}
					tabIndex={0}
					aria-label='Dine Rewards Logo'
				/>
				<div className={styles['user']}>
					<div
						data-testid='user-icon'
						className={styles['button']}
						onClick={handleClickMemberProfile}
						onKeyDown={(ev) => {
							if (ev.key === 'Enter' || ev.key === ' ') {
								ev.preventDefault();
								handleClickMemberProfile();
							}
						}}
						aria-label='Member profile'
						tabIndex={0}
					>
						<Icon icon={IconUserBonefish} customSize />
					</div>
				</div>
			</div>

			<div className={styles['item-container']}>
				<div
					data-testid='available-rewards'
					className={styles['rewards']}
					onClick={() => handleClickMenuItem('available-rewards')}
					onKeyDown={(ev) => keyDownMenuHandler(ev, 'available-rewards')}
					tabIndex={0}
				>
					<div className={styles['label']}>AVAILABLE REWARDS</div>
					<div
						className={styles['rewards-counter']}
						aria-label={`You currently have ${userRewardsInfo.userRewardsAmount} dollars in rewards!`}
					>{`\$${userRewardsInfo.userRewardsAmount}`}</div>
				</div>
			</div>

			<div className={styles['item-container']}>
				<div
					data-testid='visit-tracker'
					className={styles['points-tracker']}
					onClick={() => handleClickMenuItem('visit-tracker')}
					onKeyDown={(ev) => keyDownMenuHandler(ev, 'visit-tracker')}
					tabIndex={0}
				>
					<div className={styles['label']}>POINTS TRACKER</div>
					<div className={styles['subtitle']}>
						Only {pointsToReward - userRewardsInfo.userPoints} points till your{' '}
						<strong>next reward!</strong>
					</div>
					<div className={styles['progress-bar-container']}>
						<div
							data-testid='progress-bar'
							className={styles['progress-bar']}
							style={{ width: `${handleProgressBarPoints()}%` }}
						></div>
						<div
							className={styles['progress-bar-limits']}
							aria-label={`${pointsToReward} points are needed for a reward. You currently have ${userRewardsInfo.userPoints}!`}
						>
							<div>0</div>
							<div>{pointsToReward}</div>
						</div>
					</div>
				</div>
			</div>

			<div className={styles['menu']}>
				{menu.map(({ label, hash, icon }) => (
					<div className={styles['item-container']} key={hash}>
						<div
							data-testid={hash}
							className={styles['menu-item']}
							onClick={() => handleClickMenuItem(hash)}
							onKeyDown={(ev) => keyDownMenuHandler(ev, hash)}
							tabIndex={0}
						>
							<div className={styles['icon']}>
								<Icon icon={icon} customSize />
							</div>
							<div className={styles['label']}>{label}</div>
						</div>
					</div>
				))}
				<div className={styles['footer']}>
					<Footer renderMobile={true} />
				</div>
			</div>
		</div>
	);
};
